/**
 * Admin Intake Templates
 *
 * System templates for admin portal intake wizards.
 * These templates use the IntakeWizardScroll component for a unified UX.
 *
 * @see Business Creation - Multi-step wizard for creating new business accounts
 */

import { IntakeDisplayMode, type IntakeFlowTemplate, type IntakeStepTemplate } from './types';

// =============================================================================
// TEMPLATE IDS
// =============================================================================

export const ADMIN_INTAKE_TEMPLATE_IDS = {
  BUSINESS_CREATION: 'tpl-admin-business-creation',
} as const;

// =============================================================================
// BUSINESS CREATION TEMPLATE
// =============================================================================

/**
 * Step 1: Company Information
 * Collects basic company details including name, domain, realm, and industry
 */
const createCompanyInfoStep = (): IntakeStepTemplate => ({
  id: 'step-company-info',
  order: 0,
  name: 'Company Information',
  description: 'Enter the basic company details',
  icon: 'Business',
  requiresAuth: false,
  fields: [
    {
      id: 'field-company-name',
      name: 'name',
      label: 'Company Name',
      type: 'text',
      required: true,
      placeholder: 'Acme Corporation',
      helpText: 'The legal name of the business',
      validation: {
        minLength: 2,
        maxLength: 255,
        messages: {
          minLength: 'Company name must be at least 2 characters',
        },
      },
      width: 6,
    },
    {
      id: 'field-company-domain',
      name: 'domain',
      label: 'Company Domain',
      type: 'text',
      required: true,
      placeholder: 'acme.com',
      helpText: 'Used for email domains and subdomains',
      validation: {
        pattern: '^[a-z0-9]+([-.]{1}[a-z0-9]+)*\\.[a-z]{2,}$',
        messages: {
          pattern: 'Invalid domain format (e.g., company.com)',
        },
      },
      width: 6,
    },
    {
      id: 'field-realm',
      name: 'realmId',
      label: 'Realm',
      type: 'dynamic-select',
      required: true,
      helpText: 'Select the realm this business belongs to',
      dynamicSelectConfig: {
        dataSource: 'realms',
        valueField: 'id',
        labelField: 'name',
        includeFields: ['isSandbox'],
        emptyMessage: 'No realms available. Please create a realm first.',
        loadingMessage: 'Loading realms...',
      },
      width: 6,
    },
    {
      id: 'field-industry',
      name: 'industry',
      label: 'Industry',
      type: 'select',
      required: false,
      helpText: 'Select the primary industry',
      options: [
        { value: '', label: 'Select an industry' },
        { value: 'technology', label: 'Technology' },
        { value: 'finance', label: 'Finance' },
        { value: 'healthcare', label: 'Healthcare' },
        { value: 'retail', label: 'Retail' },
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'education', label: 'Education' },
        { value: 'real_estate', label: 'Real Estate' },
        { value: 'other', label: 'Other' },
      ],
      width: 6,
    },
    {
      id: 'field-company-size',
      name: 'companySize',
      label: 'Company Size',
      type: 'select',
      required: false,
      options: [
        { value: '1-10', label: '1-10 employees' },
        { value: '11-50', label: '11-50 employees' },
        { value: '51-200', label: '51-200 employees' },
        { value: '201-500', label: '201-500 employees' },
        { value: '500+', label: '500+ employees' },
      ],
      defaultValue: '1-10',
      width: 6,
    },
    {
      id: 'field-is-sandbox',
      name: 'isSandbox',
      label: 'Create as sandbox (for testing purposes)',
      type: 'checkbox',
      required: false,
      defaultValue: false,
      width: 6,
    },
  ],
});

/**
 * Step 2: Subscription Plan
 * Allows selection of subscription tier with pricing cards
 */
const createSubscriptionStep = (): IntakeStepTemplate => ({
  id: 'step-subscription',
  order: 1,
  name: 'Subscription Plan',
  description: 'Choose a subscription plan for this business',
  icon: 'CreditCard',
  requiresAuth: false,
  fields: [
    {
      id: 'field-subscription-tier',
      name: 'subscriptionTier',
      label: 'Select a Plan',
      type: 'pricing-cards',
      required: true,
      defaultValue: 'starter',
      pricingCardsConfig: {
        tiers: [
          {
            value: 'free',
            label: 'Free',
            description: 'Basic features for trying out',
            price: '$0/month',
            annualPrice: '$0/year',
            features: ['5 users', '1,000 API calls/month', 'Community support'],
          },
          {
            value: 'starter',
            label: 'Starter',
            description: 'For small teams getting started',
            price: '$49/month',
            annualPrice: '$470/year',
            features: ['10 users', '10,000 API calls/month', 'Email support', 'Basic analytics'],
            recommended: true,
            badge: 'Most Popular',
          },
          {
            value: 'professional',
            label: 'Professional',
            description: 'For growing businesses',
            price: '$199/month',
            annualPrice: '$1,910/year',
            features: [
              '50 users',
              '100,000 API calls/month',
              'Priority support',
              'Advanced analytics',
              'Custom integrations',
            ],
          },
          {
            value: 'enterprise',
            label: 'Enterprise',
            description: 'For large organizations',
            price: 'Custom pricing',
            features: [
              'Unlimited users',
              'Unlimited API calls',
              '24/7 support',
              'Dedicated account manager',
              'SLA guarantee',
            ],
          },
        ],
        showBillingCycleToggle: true,
        billingCycleFieldName: 'billingCycle',
      },
      width: 12,
    },
    {
      id: 'field-billing-cycle',
      name: 'billingCycle',
      label: 'Billing Cycle',
      type: 'select',
      required: true,
      defaultValue: 'monthly',
      options: [
        { value: 'monthly', label: 'Monthly' },
        { value: 'annual', label: 'Annual (20% discount)' },
      ],
      helpText: 'Save 20% with annual billing',
      width: 6,
    },
  ],
});

/**
 * Step 3: Admin User
 * Collects information about the initial admin user for this business
 */
const createAdminUserStep = (): IntakeStepTemplate => ({
  id: 'step-admin-user',
  order: 2,
  name: 'Admin User',
  description: 'Create the primary administrator for this business',
  icon: 'Person',
  requiresAuth: false,
  fields: [
    {
      id: 'field-admin-name',
      name: 'adminName',
      label: 'Admin Name',
      type: 'text',
      required: true,
      placeholder: 'John Doe',
      helpText: 'Full name of the primary business administrator',
      validation: {
        minLength: 2,
        messages: {
          minLength: 'Admin name must be at least 2 characters',
        },
      },
      width: 6,
    },
    {
      id: 'field-admin-email',
      name: 'adminEmail',
      label: 'Admin Email',
      type: 'email',
      required: true,
      placeholder: 'john@acme.com',
      helpText: 'A welcome email with login instructions will be sent to this address',
      width: 6,
    },
  ],
});

/**
 * Step 4: Review & Confirm
 * Shows a summary of all collected information before submission
 */
const createReviewStep = (): IntakeStepTemplate => ({
  id: 'step-review',
  order: 3,
  name: 'Review & Confirm',
  description: 'Review the information before creating the business',
  icon: 'CheckCircle',
  requiresAuth: false,
  fields: [
    {
      id: 'field-summary',
      name: '_summary',
      label: '',
      type: 'summary',
      required: false,
      summaryConfig: {
        sections: [
          {
            title: 'Company Information',
            fields: [
              { name: 'name', label: 'Company Name', format: 'text' },
              { name: 'domain', label: 'Domain', format: 'text' },
              { name: 'realmId', label: 'Realm', format: 'lookup' },
              {
                name: 'industry',
                label: 'Industry',
                format: 'lookup',
                lookupMap: {
                  technology: 'Technology',
                  finance: 'Finance',
                  healthcare: 'Healthcare',
                  retail: 'Retail',
                  manufacturing: 'Manufacturing',
                  education: 'Education',
                  real_estate: 'Real Estate',
                  other: 'Other',
                },
              },
              { name: 'companySize', label: 'Company Size', format: 'text' },
              { name: 'isSandbox', label: 'Sandbox Mode', format: 'boolean' },
            ],
          },
          {
            title: 'Subscription Plan',
            fields: [
              {
                name: 'subscriptionTier',
                label: 'Plan',
                format: 'lookup',
                lookupMap: {
                  free: 'Free',
                  starter: 'Starter',
                  professional: 'Professional',
                  enterprise: 'Enterprise',
                },
              },
              {
                name: 'billingCycle',
                label: 'Billing Cycle',
                format: 'lookup',
                lookupMap: {
                  monthly: 'Monthly',
                  annual: 'Annual (20% discount)',
                },
              },
            ],
          },
          {
            title: 'Admin User',
            fields: [
              { name: 'adminName', label: 'Name', format: 'text' },
              { name: 'adminEmail', label: 'Email', format: 'text' },
            ],
          },
        ],
        showEditButtons: false,
      },
      width: 12,
    },
  ],
});

/**
 * Business Creation Template
 *
 * A complete intake wizard for creating new business accounts from the Admin Portal.
 * Uses IntakeWizardScroll for a modern, scrollable UX with section status indicators.
 */
export const businessCreationTemplate: IntakeFlowTemplate = {
  id: ADMIN_INTAKE_TEMPLATE_IDS.BUSINESS_CREATION,
  name: 'Business Creation',
  description: 'Create a new business account with subscription and admin user',
  category: 'admin',
  icon: 'Business',
  color: '#1976D2',
  tags: ['admin', 'business', 'onboarding', 'subscription'],
  complexity: 'intermediate',
  industryFit: ['general'],
  data: {
    intakeType: 'general',
    version: '1.0.0',
    displayMode: IntakeDisplayMode.SCROLL,
    steps: [createCompanyInfoStep(), createSubscriptionStep(), createAdminUserStep(), createReviewStep()],
    complianceQuestions: [
      {
        id: 'terms-acknowledgment',
        question:
          'I confirm that I have the authority to create this business account and agree to the platform terms of service.',
        requiredAnswer: true,
        blockingMessage: 'You must acknowledge the terms to proceed.',
      },
    ],
    exitBehavior: 'custom',
    customExitUrl: '/businesses',
    showEstimatedTime: true,
    estimatedTimeMinutes: 5,
    successMessage: 'Business account created successfully! A welcome email has been sent to the admin user.',
  },
};

// =============================================================================
// ALL ADMIN TEMPLATES
// =============================================================================

export const adminIntakeTemplates: IntakeFlowTemplate[] = [businessCreationTemplate];
